import React, { useState, useEffect } from "react";
import { Input, DatePicker, Button } from "antd";
import { useAllOrders } from "../../../../../api/orders/orders";
import { useExportOrders } from "../../../../../api/orders/orders";
import { useLoader } from "../../../../../utils/LoaderContext";

const { RangePicker } = DatePicker;
const ORDERS_PER_PAGE = 20;

const SalesSheet = () => {
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ date range states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const { setLoading } = useLoader();

  const urlParams = new URLSearchParams(window.location.search);
  const usersid = urlParams.get("usersid");

  // ✅ export hook
  const exportOrders = useExportOrders();

  const {
    data: allOrdersData,
    isLoading,
    isError,
  } = useAllOrders({
    currentPage,
    ORDERS_PER_PAGE,
    search: searchQuery,
    startDate,
    endDate,
  });

  // search debounce
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  // const orders = allOrdersData?.orders || [];
  // const totalPages = allOrdersData?.totalPages || 1;

  const handleDateChange = (dates) => {
    if (dates) {
      setStartDate(dates[0].format("YYYY-MM-DD"));
      setEndDate(dates[1].format("YYYY-MM-DD"));
      setCurrentPage(1);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleExport = () => {
    if (!startDate || !endDate) {
      return alert("Please select a date range first!");
    }
    exportOrders.mutate({ startDate, endDate });
  };

  const orders = allOrdersData?.orders || [];
  const totalPages = allOrdersData?.totalPages || 1;
  const role = localStorage.getItem("role");
  const isAdmin = role === "admin";

  return (
    <div className="w-full mt-6">
      {/* Top filters row */}
      <div className="mb-4 flex justify-between items-center gap-4">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number, or Customer"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
          <div className="flex items-center gap-3">
            <RangePicker onChange={handleDateChange} />

            <button
              onClick={handleExport}
              disabled={!startDate || !endDate || exportOrders.isLoading}
              className="px-4 py-1 text-base rounded-lg bg-primaryLight hover:bg-primary text-primary border border-primary hover:text-white transition-colors"
            >
              {exportOrders.isLoading ? "Downloading..." : "Download Excel"}
            </button>
          </div>
      
      </div>

      {/* Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1200px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order Number / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer / Contact</th>
              <th className="p-3">Address</th>
              <th className="p-3">Courier Details</th>
              <th className="p-3">Charges</th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="text-center py-6 text-gray-500">
                  Loading all orders...
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => (
                <tr key={order._id || index} className="hover:bg-gray-50">
                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.OrderNumber}
                    </div>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                        order.shippingStatusText === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : order.shippingStatusText === "in_transit"
                          ? "bg-blue-100 text-blue-600"
                          : order.shippingStatusText === "shipped"
                          ? "bg-green-100 text-green-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                    >
                      {order.shippingStatusText}
                    </div>
                  </td>
                  <td className="p-4">
                    ₹{order.InvoiceAmount}
                    <div
                      className={`text-xs mt-1 inline-block px-2 py-1 rounded ${
                        order.PaymentType === "cod"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {order.PaymentType}
                    </div>
                  </td>
                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name}
                      </p>
                    ))}
                  </td>
                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.Contact}</p>
                  </td>
                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.City}</p>
                    <p>{order.BillingAddress?.State}</p>
                    <p>{order.BillingAddress?.Pincode}</p>
                  </td>
                  <td className="p-4">
                    <p>{order.awbNumber || "-"}</p>
                    <p>{order.courier || "-"}</p>
                  </td>
                  <td className="p-4">
                    <p> ₹{order.ShippingCharge}</p>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="text-center py-6 text-gray-400">
                  No orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || isLoading}
              className={`px-4 py-2 rounded border ${
                currentPage === 1 || isLoading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || isLoading}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages || isLoading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesSheet;
